import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { api } from '../utils/api';
import { LibraryIcon, isLibraryIcon } from '../lib/iconLibrary';
import { autoIconColor } from '../lib/colorContrast';

export default function TileCard({ tile, editable = false, onEdit, onDelete, tileColor, tileFontColor, tileFontSize, tileStyle = 'soft', tileShape = 'default', showTitle = true, showIcon = true, primaryColor = '#2563EB', onChatOpen }) {
  // Note: Text color only applies for 'soft' and 'outlined' styles
  // For 'gradient' and 'solid' styles, white text is used for readability

  // A custom uploaded image should never be cropped or masked — admins picked
  // that image deliberately. Only the small emoji/font-icon glyphs get the
  // subtle coloured "chip" behind them for visual pop.
  const isCustomImage = tile.icon && (tile.icon.startsWith('data:') || tile.icon.startsWith('http'));
  const isLibrary = isLibraryIcon(tile.icon);

  // Icon container border radius:
  //  - Custom uploaded image: matches the tile shape (no crop surprises)
  //  - Emoji/font glyph: keep the friendly rounded "chip" look
  const iconContainerRadius = isCustomImage
    ? (tileShape === 'square' ? '0.25rem' : tileShape === 'circle' ? '50%' : '0.75rem')
    : '9999px';

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (editable) return;

    // Chat tile — open in overlay, not a new tab
    if (tile.tile_type === 'chat') {
      if (onChatOpen) onChatOpen(tile);
      return;
    }

    if (tile.url) {
      try {
        await api.trackAnalyticsEvent({
          event_type: 'tile_click',
          tile_id: tile.id,
          metadata: { tile_title: tile.title }
        });
      } catch (error) {
        console.error('Failed to track tile click:', error);
      }
      window.open(tile.url, '_blank', 'noopener,noreferrer');
    }
  };
  
  // Handle touch events for iOS - fires immediately on touch
  const handleTouchEnd = (e) => {
    // Only process if it's a simple tap (not a scroll)
    if (e.cancelable) {
      handleClick(e);
    }
  };

  const getFontSizeClass = () => {
    switch (tileFontSize) {
      case 'xs': return 'text-xs sm:text-sm';
      case 'sm': return 'text-sm sm:text-base';
      case 'lg': return 'text-lg sm:text-xl';
      case 'xl': return 'text-xl sm:text-2xl';
      default: return 'text-base sm:text-lg';
    }
  };

  // Determine border radius based on tile shape
  const getBorderRadius = () => {
    switch (tileShape) {
      case 'rounded': return '1.5rem';
      case 'circle': return '50%';
      case 'square': return '0.25rem';
      default: return '0.75rem';
    }
  };

  // Helper function to lighten a color for gradient
  const lightenColor = (color, percent) => {
    // Convert hex to RGB
    let r, g, b;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      [r, g, b] = matches.map(Number);
    } else {
      return color;
    }
    
    // Lighten by moving towards white
    r = Math.min(255, Math.round(r + (255 - r) * percent));
    g = Math.min(255, Math.round(g + (255 - g) * percent));
    b = Math.min(255, Math.round(b + (255 - b) * percent));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Determine tile style - matching DraggableTile.js implementation
  // tile.color = tile's individual background color (set in Edit Tile)
  // tileColor = customer's default tile color (fallback)
  // primaryColor = customer's primary theme color (for borders, accents)
  const getTileStyle = () => {
    const baseColor = tile.color || tileColor || primaryColor || '#2563EB';
    // For soft/outlined (light backgrounds), tileFontColor is the global default.
    // Only use tile.text_color as a per-tile override when it differs from the
    // hardcoded '#FFFFFF' default (which is meaningless on light backgrounds).
    const perTileOverride = tile.text_color && tile.text_color !== '#FFFFFF' ? tile.text_color : null;

    switch (tileStyle) {
      case 'solid':
        return {
          backgroundColor: baseColor,
          color: tile.text_color || tileFontColor || '#FFFFFF'
        };
      case 'gradient':
        const lightColor = lightenColor(baseColor, 0.5);
        return {
          background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
          color: tile.text_color || tileFontColor || '#FFFFFF'
        };
      case 'outlined':
        // "Outlined" fills white by default, but an explicit per-tile background
        // colour must still paint the background rather than only the border.
        return tile.color
          ? {
              backgroundColor: baseColor,
              border: `2px solid ${tile.border_color || baseColor}`,
              color: autoIconColor(baseColor, tile.text_color)
            }
          : {
              backgroundColor: '#FFFFFF',
              border: `2px solid ${tile.border_color || baseColor}`,
              color: perTileOverride || tileFontColor || baseColor
            };
      default: // soft
        return {
          backgroundColor: `${baseColor}15`,
          color: perTileOverride || tileFontColor || baseColor
        };
    }
  };

  return (
    <Card
      className={`tile-card group relative overflow-hidden transition-all duration-200 ${
        !editable ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95' : ''
      }`}
      style={{ 
        ...getTileStyle(),
        aspectRatio: '1 / 1',
        borderRadius: getBorderRadius(),
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation'
      }}
      onClick={handleClick}
      onTouchEnd={!editable ? handleTouchEnd : undefined}
      role={!editable ? "button" : undefined}
      tabIndex={!editable ? 0 : undefined}
      data-testid={`tile-${tile.id}`}
    >
      <CardContent className="p-1 h-full flex items-center justify-center">
        <div className="tile-content flex flex-col items-center text-center justify-center gap-2 w-full h-full py-2">
          {showIcon && tile.icon && (
            <div
              className="flex-shrink-0 flex items-center justify-center overflow-hidden"
              style={{ 
                backgroundColor: (isCustomImage || isLibrary)
                  ? 'transparent'
                  : (tileStyle === 'gradient' || tileStyle === 'solid' ? 'rgba(255, 255, 255, 0.2)' : `${primaryColor}15`),
                borderRadius: iconContainerRadius,
                width: 'clamp(30px, 45%, 80px)',
                height: 'clamp(30px, 45%, 80px)',
                fontSize: 'clamp(1.5rem, 5vw, 3rem)'
              }}
            >
              {isCustomImage ? (
                <img src={tile.icon} alt="" className="w-full h-full object-contain" />
              ) : isLibrary ? (
                <LibraryIcon
                  iconString={tile.icon}
                  className="w-full h-full"
                  color={autoIconColor(tileColor || tile.color, tile.icon_color)}
                />
              ) : (
                <span>{tile.icon}</span>
              )}
            </div>
          )}
          {showIcon && tile.image_url && (
            <img
              src={tile.image_url}
              alt={tile.title}
              className="flex-shrink-0 object-contain"
              style={{
                width: 'clamp(30px, 45%, 80px)',
                height: 'clamp(30px, 45%, 80px)',
                borderRadius: iconContainerRadius
              }}
            />
          )}
          {showTitle && (
            <h3 
              className={`font-semibold leading-tight line-clamp-2 w-full overflow-hidden px-0.5 ${getFontSizeClass()}`}
              style={{ 
                color: getTileStyle().color
              }}
            >
              {tile.title || (editable ? <span className="opacity-50 italic">(untitled)</span> : '\u00A0')}
            </h3>
          )}
        </div>
        {editable && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(tile);
              }}
              className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              data-testid={`edit-tile-${tile.id}`}
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(tile.id);
              }}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
              data-testid={`delete-tile-${tile.id}`}
            >
              Delete
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}